/* =========================================================================
   Game controller — the round lifecycle, play loop, reveal, source popover,
   next-round difficulty picker, and results. Typed port of the prototype's
   controller; the mini-games, overlays, audio and pure engine live in their own
   modules and are wired through the shared `ctx`.
   ========================================================================= */
import { byId, show } from './dom';
import { ctx, resetGame } from './context';
import { addAchievement, serverKeyFor } from './achievements';
import {
  RoundEngine,
  fmtCents,
  fmtDelta,
  esc,
  HARD_START_CENTS,
  FLOOR_CENTS,
  type AnswerResult,
  type Question,
} from './engine';
import { ROUNDS, type BreakType } from './data/packs';
import { HARD_PACK } from './data/hardPack';
import { medalFor } from './medals';
import { icon, isIconName } from './ui/icons';
import { unlockAudio, sndTick, sndCorrect, sndWrong, applause } from './audio';
import { triggerAngel, triggerDidYouKnow, triggerFeastAnnouncement, openRegisterPrompt } from './overlays';
import { dykShouldFire } from './dyk';
import {
  feastAnnouncementEligible,
  shouldShowFeastAnnouncement,
  markFeastAnnounced,
  wasAnnouncedToday,
} from './feastAnnouncement';
import { etDateString } from './today';
import { shouldEaseRound, warmupPool, roundPool, recordGameCompleted, getGamesCompleted } from './easyStart';
import { registerPromptShouldFire } from './registerPrompt';
import { track, setTelemetrySession } from './telemetry';
import { recordServedCode, flushServed, resetServedLog } from './servedLog';
import { setPostAuthResume, consumePostAuthResume } from './postAuthResume';
import { unlockArcade, isArcadeUnlocked } from './arcade';
import { showBonusWheel, resetWheel } from './minigames/wheel';
import { showManna } from './minigames/manna';
import { showMatch3 } from './minigames/match3';
import { showMaryMatch3 } from './minigames/maryMatch3';
import {
  completeSession,
  isAuthenticated,
  getProfileMe,
  startGameSession,
  getFeastMoment,
  getRoundQuestions,
  getRoundOptions,
  getActiveGameSession,
  postRoundComplete,
  type StreakMilestoneReward,
} from './api';
import { pickerEligible, showRoundPicker, type RoundOptions } from './roundPicker';
import { syncFishSchool, isChosenCategory, isChosenRound } from './ui/fishSchool';
import { syncArkParade, isBibleStoriesRound } from './ui/arkParade';
import {
  loadRoundQuestions,
  saveRoundAndCelebrate,
  questionCode,
  resetSyntheticIds,
  withTimeout,
  type GameResumeState,
} from './serverGame';
import { recordAnswer, flushAnswers } from './answerQueue';
import { forgetProgress } from './progressClient';
import { showXpTally } from './ui/xpTally';
import { showLevelUp } from './ui/levelUp';
import { renderJourneyResults } from './journey';
import { showRegister, showHome } from './auth';
import { setPendingSummary, clearPendingSummary } from './pendingSummary';
import { CLIENT_VERSION } from './version';
import './characters/theo'; // side-effect: registers Brother Theo before any getLine()
import { getLine } from './characters/registry';
import { triggerCharPop, triggerRankUp, triggerTheoIntro } from './characters/ui';
import { isTheoIntroduced, markTheoIntroduced } from './progression';
import { correctPopShouldFire } from './characters/logic';
import { hintAllowed, hintVisibleFor, pickHintRemovals } from './hint';
import { momentDirector, type MomentKind } from './momentDirector';
import { triggerGtkyMoment, gtkyPoolRemaining } from './gtky';
import { gtkyShouldOffer, gtkyEngagement, readLastAskedDay, dayStamp, setGtkyEngagement } from './gtkyCadence';
import { showBreakCard, BREAK_SKIP_AWARD } from './breakCard';
import { runPrayerBreak } from './prayerBreak';
import { rankForXp, type Rank } from './data/ranksData';
import { writeSnapshot, clearSnapshot, readSnapshot, currentIdentity, type GameSnapshot } from './resumeGame';
import { setHudRound, setHudBank, setHudStreak, hudProgress } from './ui/playHud';
import { createLeaveSheet, mountLeaveSheet, renderLeaveSheet, type LeaveInfo } from './ui/leaveRound';
import { milestoneFor, showStreakMilestone } from './ui/streak';

let renderTimer: ReturnType<typeof setInterval> | null = null;
let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
let advanceTimer: ReturnType<typeof setTimeout> | null = null;
let answered = false;
let lastTickSecond = -1;
let angelShownThisRound = false; // the Sleeping Angel gag plays once per round
let dykFired = 0; // "Did You Know?" facts shown this game (see dyk.ts thresholds)
let prerollIv: ReturnType<typeof setInterval> | null = null;
let prerollTo: ReturnType<typeof setTimeout> | null = null;
let nrChosen = false;
let pendingHard = false; // difficulty choice for the round about to begin (🔥 hard)
const hardServed = new Set<number>(); // hard-pack question ids served this game (no repeats)
let registerPromptShown = false; // one-time guest register nudge (see registerPrompt.ts)
let lastPromptCumulative = 0; // cumulative count at the moment the prompt fired (telemetry)
let correctRun = 0; // consecutive in-game correct answers (drives Theo's correct pop)
let theoPopsThisGame = 0; // correct-run pops shown this game (cap 4/game, see logic.ts)
let hintUsedThisRound = false; // Theo's 50/50 is free once per round
let serverSessionId: string | null = null; // authed server session for this game (break-result / telemetry tagging)
let sessionReady: Promise<void> | null = null; // resolves once /game/session has answered (or failed)
let careerLevel = 0; // persistent career level from /game/session (0 = unknown / guest / offline)
let pendingServerResume: GameResumeState | null = null; // server save point adopted by the run being started
let roundOptionsReady: Promise<RoundOptions | null> | null = null; // round picker eligibility+groups, fetched ONCE per game (null = locked)
let currentRoundHard = false; // was the round in progress played in 🔥 hard mode?
let roundCompleting = false; // onRoundComplete is in flight — a second nextQuestion() at the boundary must not re-run it
let nextRoundTimer: ReturnType<typeof setTimeout> | null = null; // startNextRound's 1.4s launch beat (see abortPendingRound)
let runSerial = 0; // bumped per prepareRun: a /game/session reply for an abandoned run must never bind to this one
let runRoundIndex = -1; // the round this run committed to (-1 until beginRound) — gates a LATE session reply
let xpAwardedThisGame = 0; // XP banked by /round-complete this run — the results rank-up needs the whole game's award
/** The category the player picked for this round ('the-chosen', …), or null
    for Mixed. Only consumer today is the round intro's ichthys school. */
let roundGroupKey: string | null = null;

/** The 🔥 toggle's honest subtitle (PLAN-gameplay-v2 §3b): the jump used to be
    the whole complaint, so the stakes are telegraphed BEFORE the opt-in. */
export const HARD_MODE_SUBTITLE = 'Doctor-of-the-Church depth · $1,500 questions · noticeably harder.';

/** A hard round the player clearly lost — Theo answers it with a word, never
    silence (PLAN-gameplay-v2 §3b). PURE. */
export const HARD_ROUND_LOST_ACCURACY = 0.5;
export function hardRoundLost(hard: boolean, correct: number, answered: number): boolean {
  return hard && answered > 0 && correct / answered < HARD_ROUND_LOST_ACCURACY;
}

/** The server session id for the current authed game, if one was opened. */
export function currentServerSession(): string | null {
  return serverSessionId;
}

/** The player's career level as last reported by the server (0 = unknown). */
export function currentCareerLevel(): number {
  return careerLevel;
}

/** Forget the career level on sign-out / identity change. It is module memory,
    only ever RAISED by the server, so without this a guest on a shared device
    inherited the previous owner's "Level 12" chip, skipped the beginner warm-up
    and passed the GTKY engaged gate. prepareRun also zeroes it for guests. */
export function resetCareerLevel(): void {
  careerLevel = 0;
}

/** May a /game/session reply that arrives LATE (after the run committed to a
    round — awaitSession timed out) bind its session to the run? A fresh session
    (no save point) is harmless: later rounds simply post to it. A save point at
    ANOTHER round is not — binding it would replay rounds the server already
    banked (zero XP) and hand the next resume a stale round. PURE. */
export function lateSessionUsable(resume: GameResumeState | null, runRound: number): boolean {
  if (!resume) return true;
  if (runRound < 0) return true; // the run has not committed yet — startOrResume adopts the save point
  return resume.roundIndex === runRound;
}

/** The rank reached by the END of a game when it is higher than the rank the
    game STARTED at — else null. `startXp` is the profile XP captured at the
    first round intro of the run; when that fetch failed, the XP banked this
    game (per-round awards + the completion bonus) is subtracted back instead.
    Comparing against only the completion bonus never fired: the per-round
    awards were already in `xpNow`. PURE. */
export function rankUpAfterGame(startXp: number | null, xpNow: number, awardedThisGame: number): Rank | null {
  const before = rankForXp(startXp ?? Math.max(0, xpNow - awardedThisGame));
  const after = rankForXp(xpNow);
  return after.level > before.level ? after : null;
}

/** The streak milestone to show on the RESULTS screen, from the legacy
    /complete-summary reply. A game whose rounds were saved server-side had its
    milestone paid AND shown at /round-complete (db/050 pays once per streak),
    so re-deriving one here would show it a second time, "unpaid". Only a run
    that never opened a server session still needs the client-side guess. PURE. */
export function resultsStreakMilestone(
  res: { streakMilestone?: StreakMilestoneReward | null; currentStreak?: number },
  perRoundSaved: boolean,
  startStreak: number | null,
): { days: number; reward: StreakMilestoneReward | null } | null {
  const paid = res.streakMilestone ?? null;
  if (paid) return { days: paid.days, reward: paid };
  if (perRoundSaved) return null;
  const now = res.currentStreak;
  if (typeof now !== 'number') return null;
  const days = milestoneFor(startStreak ?? now, now);
  return days ? { days, reward: null } : null;
}

/** How long a round will wait on the server before playing bundled packs. Kept
    well under the api client's 8s request timeout: a slow server must never make
    the player stare at a dead screen between rounds. */
const ROUND_FETCH_TIMEOUT_MS = 2_500;
const SESSION_WAIT_MS = 2_000;
/** Longest a round start will wait on the round-options fetch (it launches with
    the session open, so by any round boundary it has almost always settled). A
    slow/absent endpoint → locked → today's flow, never a stalled round. */
const OPTIONS_WAIT_MS = 2_000;

const PREROLL_TICK_MS = 800; // per-number tick for the 3-2-1 dial, section-starts only
// 2026-08-04 (Eric: the 3-2-1 dial "is causing anxiety" appearing before every single
// question). The dial now shows only at each section's Q1 (engine.idx === -1, below);
// every other question gets a brief, number-free pause instead — the old dial's total
// (3 x PREROLL_TICK_MS = 2400ms) minus 1s, per Eric's "lower by 1 second."
const PREROLL_QUIET_MS = 3 * PREROLL_TICK_MS - 1000;
const REVEAL_MS = 5000; // linger on the verdict + source before auto-advancing
const REVEAL_RESUME_MS = 1600; // brief beat after closing the source popover

/* ---------- Bank pill UI ---------- */
export function updateBank(): void {
  const engine = ctx.engine;
  if (!engine) return;
  byId('bankVal').textContent = fmtCents(engine.bankCents);
  byId('bankPill').classList.toggle('neg', engine.bankCents < 0);
  setHudBank(engine.bankCents); // the play HUD's 💰 cell mirrors the pill (bankbar is hidden on play)
}

let floatDeltaTo: ReturnType<typeof setTimeout> | null = null;
export function floatDelta(c: number): void {
  // Two homes: the bankbar pill (pause screens) and the play HUD's 💰 cell.
  const targets = [byId('floatDelta'), document.getElementById('hudFloatDelta')].filter((x): x is HTMLElement => !!x);
  for (const f of targets) {
    f.textContent = fmtDelta(c);
    f.className = 'float-delta ' + (c >= 0 ? 'pos' : 'neg');
    void f.offsetWidth;
    f.classList.add('run');
  }
  if (floatDeltaTo) clearTimeout(floatDeltaTo); // a quick second award must not clip this early
  floatDeltaTo = setTimeout(() => targets.forEach((f) => f.classList.remove('run')), 1500);
}

// Wire the shared context so mini-games / overlays can drive bank + achievements.
ctx.updateBank = updateBank;
ctx.floatDelta = floatDelta;
ctx.addAchievement = (icon, name, desc) => addAchievement(ctx.game, icon, name, desc);
ctx.nextQuestion = nextQuestion;

/* ---------- Game / round lifecycle ---------- */

/** Shared init for BOTH fresh starts and resumes: everything that must reset for
    any run, WITHOUT touching the game state a resume restores (log/bank/round +
    the per-game counters the snapshot carries). */
function prepareRun(): void {
  gameStartStreak = null; // a new run: re-baseline the streak for the milestone check
  gameStartXp = null; // …and the XP for the rank-up check
  xpAwardedThisGame = 0;
  roundCompleting = false;
  runRoundIndex = -1;
  abortPendingRound(); // a "Loading…" beat from an abandoned run must not launch its round over this one
  unlockAudio();
  stopTimers(); // defensive: no start path currently runs mid-question, but a live timer here would double-serve
  resetWheel();
  resetServedLog(); // fresh served-question dedupe set (prior rounds already flushed)
  correctRun = 0;
  hintUsedThisRound = false;
  pendingHard = false;
  setPostAuthResume(null); // drop any stale mid-game resume from an aborted run
  ctx.arcadeMode = false; // defensive: a real game must never inherit sandbox suppression
  clearPendingSummary(); // a new game invalidates the previous tally's save offer
  // The Moment Director's session clocks start HERE, so a run opens with ~90s of
  // unbroken play before any interstitial and ~10 min before the first break.
  // (The lifetime answered / prayer counters it also owns are NOT reset — they
  // are rolling, per identity, and survive between runs.)
  momentDirector.beginSession();
  byId('bankbar').classList.remove('hidden');

  // Authed: open a server session so in-game events carry a real sessionId, the
  // round can ask the server for ITS questions (/game/round-questions), answers
  // can post to /game/answer, and each round boundary can save + bank XP. Guests
  // skip entirely — the whole loop stays playable offline on bundled packs.
  serverSessionId = null;
  setTelemetrySession(null);
  // NOTE: the answer queue is deliberately NOT reset here — rows carry their own
  // sessionId, and the previous game's final-round retries would otherwise be
  // dropped. It is reset on sign-out only (accountMenu.scrubAuthedDeviceState).
  resetSyntheticIds();
  pendingServerResume = null;
  sessionReady = null;
  roundOptionsReady = null;
  if (!isAuthenticated()) careerLevel = 0; // a guest never carries a signed-out account's level
  if (isAuthenticated()) {
    const run = ++runSerial;
    sessionReady = startGameSession(ROUNDS.length).then((r) => {
      if (!r.ok) return; // offline / 404 / migration pending → bundled packs, silently
      if (run !== runSerial) return; // a reply for a run since abandoned — never bind it to this one
      if (!lateSessionUsable(r.data.resume, runRoundIndex)) {
        // Arrived after awaitSession gave up and the run started at another
        // round: binding would replay rounds already banked (zero XP).
        track('SessionLateIgnored', { round: runRoundIndex + 1, serverRound: r.data.resume!.roundIndex + 1 });
        return;
      }
      serverSessionId = r.data.sessionId;
      setTelemetrySession(r.data.sessionId);
      if (r.data.careerLevel > 0) careerLevel = r.data.careerLevel;
      if (r.data.resume) pendingServerResume = r.data.resume;
    });
    // Round picker eligibility + category groups: ONE fetch per game, kicked off
    // in parallel with the session open so round 1 rarely waits on it. Every
    // failure (offline / 401 / 404 / endpoint not built yet) → null = locked =
    // exactly today's flow. Refetched next game so counts/unlock stay fresh.
    roundOptionsReady = getRoundOptions()
      .then((r) => (r.ok ? r.data : null))
      .catch(() => null);
  }
}

/** Wait (briefly) for the session call so round 1 can be server-served. Never
    rejects and never blocks past SESSION_WAIT_MS. */
async function awaitSession(): Promise<void> {
  if (!sessionReady) return;
  await withTimeout(sessionReady, SESSION_WAIT_MS, undefined);
}

/** Per-game counters for a run that starts at Round 1. */
function freshRunState(): void {
  dykFired = 0;
  registerPromptShown = false; // re-arm the one-time guest nudge
  theoPopsThisGame = 0;
  hardServed.clear();
  clearSnapshot(); // starting FRESH invalidates any saved between-rounds run
}

export function startGame(): void {
  void startGameAsync();
}

async function startGameAsync(): Promise<void> {
  prepareRun();
  resetGame();
  freshRunState();
  pendingServerResume = null; // an explicit fresh start abandons any server save point
  track('GameStarted');
  await beginRound(0);
}

/** Home Start CTA: resume the saved between-rounds run when one exists for the
    current identity, else a fresh game. (renderResumeCta labels the button.)
    Source of truth for a signed-in player is the SERVER save point returned by
    POST /api/game/session (survives reinstalls + syncs across devices); the
    localStorage snapshot remains the guest path and the offline fallback, and
    supplies the prior rounds' log when it lines up with the server's round. */
export function startOrResume(): void {
  void startOrResumeAsync();
}

async function startOrResumeAsync(): Promise<void> {
  const local = readSnapshot(currentIdentity());
  prepareRun();
  resetGame();
  if (isAuthenticated()) await awaitSession();
  // Secondary probe, only when the device thinks a run is in progress but the
  // session response carried no save point: reconciles a server build that
  // reports its open session on GET /game/session/active only. Costs nothing on
  // the ordinary "start fresh" path (no local snapshot → no probe).
  if (isAuthenticated() && local && !pendingServerResume) {
    const active = await withTimeout(getActiveGameSession(ROUNDS.length), SESSION_WAIT_MS, {
      ok: false as const,
      offline: true,
      error: 'timeout',
    });
    if (active.ok && active.data) pendingServerResume = active.data;
  }
  const server = pendingServerResume;
  // A server save point can be HOLLOW: /game/round-questions advances
  // CurrentRoundIndex without banking (usp_Session_SaveProgress leaves a NULL
  // bank untouched), and sessions opened against an older API predate
  // round-completes entirely. Live incident 2026-08-16: a stale server save
  // (round 2, $0) silently beat a fresher local run (round 3, $12,780).
  // The richer record wins; ties go to the server (it survives reinstalls).
  const localIsRicher =
    !!server &&
    !!local &&
    (local.nextRoundIndex > server.roundIndex ||
      (local.nextRoundIndex === server.roundIndex && local.bankCents > server.bankCents));
  if (server && !localIsRicher) {
    // Same round → the local snapshot's log/counters still describe this run.
    const merged = local && local.nextRoundIndex === server.roundIndex ? local : null;
    await restoreRun(merged, server);
    return;
  }
  if (local) {
    await restoreRun(local, null);
    return;
  }
  freshRunState();
  track('GameStarted');
  await beginRound(0);
}

/** Restore a run in progress: prior rounds' log/bank + the per-game counters,
    then begin the next round. `server` (when present) is authoritative for the
    round index, the bank and the hard-mode state; `snap` fills in the local
    colour (log + counters) it cannot carry. Mid-round exits only ever lose the
    round in progress — a save point only exists BETWEEN rounds. */
async function restoreRun(snap: GameSnapshot | null, server: GameResumeState | null): Promise<void> {
  hardServed.clear();
  if (snap) {
    ctx.game.bank = snap.bankCents;
    ctx.game.log = snap.log;
    dykFired = snap.dykFired;
    theoPopsThisGame = snap.theoPopsThisGame;
    registerPromptShown = snap.registerPromptShown;
    for (const id of snap.hardServed) hardServed.add(id);
  } else {
    dykFired = 0;
    theoPopsThisGame = 0;
    registerPromptShown = false;
  }
  const nextRoundIndex = server ? server.roundIndex : snap!.nextRoundIndex;
  if (server) {
    ctx.game.bank = server.bankCents; // server bank wins over the device snapshot
    serverSessionId = server.sessionId;
    setTelemetrySession(server.sessionId);
    pendingHard = server.hardMode;
  } else if (snap) {
    pendingHard = snap.hard === true; // device-only resume: v2 snapshots carry the round's hard flag (v1: false)
  }
  track('GameResumed', {
    round: nextRoundIndex + 1,
    bank: ctx.game.bank,
    source: server ? 'server' : 'device',
  });
  await beginRound(nextRoundIndex);
}

/** Persist the between-rounds snapshot (identity-scoped; see resumeGame.ts). */
function saveRunSnapshot(nextRoundIndex: number): void {
  // Debt is forgiven at every save point (Eric 2026-08-21: "make negative
  // vanish"). A bank can dip below $0 WITHIN a round, but nothing negative is
  // ever persisted — this matches the server, whose /round-complete clamps the
  // posted bank at 0, so device and server save points can never disagree on
  // sign (the old "-$700 banked" Resume label came from exactly that mismatch).
  if (ctx.game.bank < 0) ctx.game.bank = 0;
  writeSnapshot({
    v: 2,
    identity: currentIdentity(),
    nextRoundIndex,
    bankCents: ctx.game.bank,
    log: ctx.game.log.slice(),
    dykFired,
    theoPopsThisGame,
    registerPromptShown,
    hardServed: Array.from(hardServed),
    hard: currentRoundHard, // v2: a device-only resume keeps 🔥 hard mode (the server path always did)
    savedAtIso: new Date().toISOString(),
  });
}

/** The bundled-pack questions for a round — the OFFLINE / GUEST / fallback path,
    unchanged from the BETA behaviour (hard pack · beginner warm-up · per-round
    hardness cap). Pure apart from the pack imports. */
function bundledRoundQuestions(idx: number, hard: boolean): { questions: Question[]; warmup: boolean } {
  const rc = ROUNDS[idx];
  // 🔥 Hard mode: serve Level-3 questions with no repeats across the game. If the
  // hard pool runs low (< round count remaining), fall back to the round's normal
  // pack for the shortfall so a round always has enough questions (crash-proof).
  let questions: Question[] = rc.pack.questions;
  if (hard) {
    const remaining = HARD_PACK.questions.filter((q) => !hardServed.has(q.id));
    questions = remaining.length >= rc.count ? remaining : remaining.concat(rc.pack.questions);
    // Level-3 questions carry a richer starting value ($1,500 vs $1,000); the
    // fallback normal questions (ids < 3001) keep the standard value.
    questions = questions.map((q) => (q.id >= 3001 ? { ...q, valueCents: HARD_START_CENTS } : q));
  }
  // Beginner warm-up: a TRUE beginner's Round 1 draws only gentle categories —
  // career level ≤ 1 and no game finished on this device (PLAN-progression §6).
  // Never combines with hard mode (the picker doesn't exist before Round 2).
  const warmup = !hard && shouldEaseRound(idx, careerLevel, getGamesCompleted());
  if (warmup) questions = warmupPool(questions, rc.count);
  // Difficulty progression (Eric 2026-07-24): every non-hard round caps question
  // hardness by round index (R1 easy-only … R4-5 full range) — see easyStart.roundPool.
  // Composes with the warm-up (both filter, both top up crash-proof); 🔥 hard mode
  // bypasses it (explicit player choice).
  if (!hard) questions = roundPool(questions, idx, rc.count);
  return { questions, warmup };
}

/** Start a round. GUESTS take the wholly synchronous bundled path — byte-for-byte
    today's experience, with no added await between tapping and the round intro.
    Signed-in players go through the server (see beginRoundAsync). Returning the
    promise lets callers await the authed path; `void beginRound()` is fine too. */
function beginRound(idx: number): Promise<void> | void {
  runRoundIndex = idx; // from here a late session reply at another round is ignored (lateSessionUsable)
  const hard = pendingHard; // set by the previous between-rounds difficulty pick
  if (!isAuthenticated()) {
    const b = bundledRoundQuestions(idx, hard);
    roundGroupKey = null; // guests never see the picker — clear the last round's pick
    renderRound(idx, hard, b.questions, b.warmup, 'fallback');
    return;
  }
  return beginRoundAsync(idx, hard);
}

async function beginRoundAsync(idx: number, hard: boolean): Promise<void> {
  const rc = ROUNDS[idx];
  await awaitSession(); // the session id must exist before we can ask for questions

  // "What shall we study today?" — unlocked players (≥ 10 lifetime completed
  // rounds, server-decided) pick the round's category HERE, before the server
  // draw, because the choice shapes that draw. Locked / offline / slow / absent
  // endpoint → groupKey stays null and the flow below is exactly today's. The
  // pick is pure preference: it only flavours the server draw — stakes, XP and
  // difficulty are untouched, and the bundled-pack fallback ignores it.
  let groupKey: string | null = null;
  if (roundOptionsReady) {
    const options = await withTimeout(roundOptionsReady, OPTIONS_WAIT_MS, null);
    if (pickerEligible(options)) {
      track('RoundPickerShown', { round: idx + 1 });
      groupKey = await showRoundPicker({
        roundNumber: idx + 1,
        totalRounds: ROUNDS.length,
        roundName: rc.name,
        careerChip: careerChipText(),
        groups: options!.groups,
      });
      track('RoundPickerChoice', { round: idx + 1, group: groupKey ?? 'mixed' });
    }
  }

  let warmup = false;
  let servedBy: 'server' | 'fallback' = 'fallback';
  // The server serves the round when it can (career-level difficulty mix + the
  // spaced-repetition ordering that ends silent repeats); the bundled packs are
  // the fallback at every rung — guest, offline, 404/503, slow, or empty payload.
  const questions = await loadRoundQuestions(
    {
      isAuthed: () => isAuthenticated(),
      sessionId: () => serverSessionId,
      fetchQuestions: getRoundQuestions,
      fallback: () => {
        const b = bundledRoundQuestions(idx, hard);
        warmup = b.warmup;
        return b.questions;
      },
      timeoutMs: ROUND_FETCH_TIMEOUT_MS,
      onSource: (source) => {
        servedBy = source;
      },
    },
    { roundIndex: idx, count: rc.count, hardMode: hard, groupKey },
  );
  roundGroupKey = groupKey;
  renderRound(idx, hard, questions, warmup, servedBy);
}

/** Build the engine for the round's questions and paint the round intro. */
function renderRound(
  idx: number,
  hard: boolean,
  questions: Question[],
  warmup: boolean,
  servedBy: 'server' | 'fallback',
): void {
  const rc = ROUNDS[idx];
  ctx.game.roundIndex = idx;
  currentRoundHard = hard;
  roundCompleting = false; // a fresh round boundary lies ahead
  setHudRound(idx, rc.name); // play HUD pill: "Round N · <name>"
  refreshHudStreak(); // 🔥 cell (authed only; cached per game)
  angelShownThisRound = false;
  hintUsedThisRound = false; // Theo's 50/50 re-arms each round
  ctx.engine = new RoundEngine(questions, rc.count, ctx.game.bank);
  // Remember which hard questions were actually served (order is the shuffled slice).
  if (hard) ctx.engine.order.forEach((q) => { if (q.id >= 3001) hardServed.add(q.id); });
  updateBank();
  renderCareerLevelLine();
  byId('riIcon').textContent = rc.icon;
  byId('riNum').textContent = `Round ${idx + 1} of ${ROUNDS.length}`;
  byId('riName').innerHTML = esc(rc.name) + (hard ? ` ${icon('flame', 22)} Hard` : '');
  // 🔥 Hard mode serves the cross-topic Level-3 pack, NOT this round's themed
  // questions — so the themed subtitle ("Noah, Jonah & more") would misdescribe
  // the round (QA bug: advertised Bible stories, got Jansenism/Constantinople).
  byId('riSub').textContent = hard ? 'Level 3. The hardest questions, drawn from every topic' : rc.subtitle;
  const breakLabel =
    rc.breakType === 'manna'
      ? 'Manna break at the half'
      : rc.breakType === 'match3'
        ? 'Faith Match at the half'
        : rc.breakType === 'marymatch3'
          ? 'Mother Mary Match-3 (3 tiers) at the half'
          : 'Bonus wheel at the half';
  byId('riMeta').textContent = `${rc.count} questions · ${breakLabel}`;
  byId('beginRoundBtn').textContent = idx === 0 ? 'Begin Round' : 'Next round';
  // Final-round cameo: Theo appears with a word before the last stretch.
  const cameo = document.getElementById('riCameo');
  if (cameo) {
    if (idx === ROUNDS.length - 1) {
      cameo.textContent = getLine('theo', 'cameo');
      cameo.classList.remove('hidden');
      track('TheoShown', { moment: 'cameo', character: 'theo' });
    } else {
      cameo.classList.add('hidden');
    }
  }
  // The Chosen's fish drift behind the intro when the ROUND is Chosen content —
  // either the player picked it, or it's Round 3, which is Chosen for everyone.
  syncFishSchool(
    document.getElementById('screenRoundIntro'),
    isChosenRound(roundGroupKey, questions.map((q) => q.category)),
  );
  // …and Noah's parade behind a Bible Stories intro (round-level gate — its
  // questions carry story-name categories, so the round is the unit).
  syncArkParade(document.getElementById('screenRoundIntro'), isBibleStoriesRound(rc.name, roundGroupKey));
  track('RoundStarted', { round: idx + 1, name: rc.name, hard, warmup, served: servedBy, level: careerLevel });
  show('roundintro');
  roundIntroConfetti(rc.icon);
}

/** The career chip's text ("Level 12"), or null for guest / offline /
    pre-Phase-1 server. Shared by the round-intro chip and the round picker's
    header so the two can never disagree. Rank titles were dropped from the
    chip (Eric 2026-08-16 — "users are not sure what Lamplighter means");
    titles now appear only at rank-up ceremonies. "Level" spelled out, never
    "Lv" (Eric 2026-08-16) — matching the home badge. */
function careerChipText(): string | null {
  if (careerLevel <= 0) return null;
  return `Level ${careerLevel}`;
}

/** Career badge on the round intro — never a cold start (PLAN-progression §5.2).
    Rendered as a CHIP ("Level 12", the .ri-level pill), never a bare
    "LEVEL 12" heading: beta feedback showed that next to "ROUND 2 OF 5" it read
    as a second position counter. Identity chip vs. position heading.
    The node is created on demand so no screen markup has to change. */
function renderCareerLevelLine(): void {
  const num = document.getElementById('riNum');
  if (!num || !num.parentElement) return;
  let el = document.getElementById('riLevel');
  if (!el) {
    el = document.createElement('span');
    el.id = 'riLevel';
    el.className = 'ri-level'; // styled by the .ri-level rule (gold pill) — no inline styles
    num.parentElement.insertBefore(el, num);
  }
  const chip = careerChipText();
  if (chip) {
    el.textContent = chip;
    el.classList.remove('hidden');
  } else {
    el.textContent = '';
    el.classList.add('hidden'); // guest / offline / pre-Phase-1 server: show nothing
  }
}

function roundIntroConfetti(roundIcon: string): void {
  const wrap = byId('riConfetti');
  wrap.innerHTML = '';
  for (let i = 0; i < 14; i++) {
    const s = document.createElement('div');
    s.className = 'rc';
    const px = Math.round(15 + Math.random() * 16);
    // Alternate the round's own glyph with brand sparkle / star line icons.
    if (i % 2 === 0) s.textContent = roundIcon;
    else s.innerHTML = icon(i % 4 === 1 ? 'sparkle' : 'star', px);
    s.style.left = (5 + Math.random() * 90).toFixed(1) + '%';
    s.style.fontSize = px + 'px';
    s.style.animationDelay = (Math.random() * 0.55).toFixed(2) + 's';
    s.style.setProperty('--rcr', (Math.random() * 360 - 180).toFixed(0) + 'deg');
    wrap.appendChild(s);
  }
}

/** Full-value celebration (Eric 2026-09-06): banking the whole $1,000 rains a
    short confetti burst over the play screen — same .rc/rcFall particles as the
    round intro, in a throwaway host so repeated wins never stack elements. */
function thousandConfetti(): void {
  const play = document.getElementById('screenPlay');
  if (!play) return;
  play.querySelector('.play-confetti')?.remove();
  const wrap = document.createElement('div');
  wrap.className = 'play-confetti';
  wrap.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 16; i++) {
    const s = document.createElement('div');
    s.className = 'rc';
    const px = Math.round(15 + Math.random() * 16);
    s.innerHTML = icon(i % 2 === 0 ? 'sparkle' : 'star', px);
    s.style.left = (5 + Math.random() * 90).toFixed(1) + '%';
    s.style.fontSize = px + 'px';
    s.style.animationDelay = (Math.random() * 0.55).toFixed(2) + 's';
    s.style.setProperty('--rcr', (Math.random() * 360 - 180).toFixed(0) + 'deg');
    wrap.appendChild(s);
  }
  play.appendChild(wrap);
  setTimeout(() => wrap.remove(), 2600); // rcFall runs 1.7s + max 0.55s delay
}

async function onRoundComplete(): Promise<void> {
  const engine = ctx.engine!;
  const roundIndex = ctx.game.roundIndex;
  const rc = ROUNDS[roundIndex];
  const qs = engine.log.filter((r) => !r.bonus);
  if (qs.length && qs.every((r) => r.wasCorrect))
    addAchievement(ctx.game, 'sparkle', 'Perfect Round: ' + rc.name, 'Every question right in ' + rc.name);
  engine.log.forEach((r) => {
    r.round = rc.name;
    r.roundIcon = rc.icon;
    ctx.game.log.push(r);
  });
  ctx.game.bank = engine.bankCents;
  void flushServed(); // post the round's served-question codes
  void flushAnswers(); // and any per-answer rows still queued (fire-and-forget)

  // Per-round server save + XP (PLAN-progression §2): a single round must move
  // the bar on its own. Guests skip entirely; a failed save is silent and the
  // game moves on unchanged.
  if (isAuthenticated() && serverSessionId) {
    const saved = await saveRoundAndCelebrate(
      {
        post: postRoundComplete,
        showXpTally,
        showLevelUp,
        onCareerLevel: (lvl) => {
          careerLevel = lvl;
          // The home badge memoises /game/progress — drop it so the next home
          // paint shows the just-awarded XP instead of a stale bar.
          forgetProgress();
        },
      },
      {
        sessionId: serverSessionId,
        roundIndex,
        correctCount: qs.filter((r) => r.wasCorrect).length,
        answeredCount: qs.length,
        bankCents: ctx.game.bank,
      },
    );
    if (saved) {
      xpAwardedThisGame += saved.xpAwarded;
      // Streak milestone paid by THIS round (db/050 pays once, at /round-complete):
      // the moment plays here or the player never sees it paid — the results
      // screen's /complete-summary reply carries nothing for it. The XP tally
      // and level-up have already resolved above, so no overlay is up.
      const ms = saved.streakMilestone;
      if (ms) {
        track('StreakMilestone', { days: ms.days, from: gameStartStreak, paid: true });
        try {
          await showStreakMilestone(ms.days, ms);
        } catch {
          /* an overlay that fails must never strand the player between rounds */
        }
      }
    }
  }
  // Losing a hard round earns a word, not silence (PLAN-gameplay-v2 §3b).
  if (isTheoIntroduced() && hardRoundLost(currentRoundHard, qs.filter((r) => r.wasCorrect).length, qs.length)) {
    track('TheoShown', { moment: 'hard-encourage', character: 'theo' });
    triggerCharPop(getLine('theo', 'hard-encourage'));
  }

  if (ctx.game.roundIndex < ROUNDS.length - 1) {
    // Round-boundary persistence: every between-rounds moment saves the rounds
    // completed so far, so leaving mid-game resumes from here.
    saveRunSnapshot(ctx.game.roundIndex + 1);
    // NOTE: the Director is deliberately NOT consulted at the round boundary.
    // Everything that happens here — the XP tally, a level-up, the difficulty
    // pick — is a ROUND MECHANIC, which §3.0 exempts from arbitration and always
    // runs; and §3a puts the prayer break at a QUESTION boundary specifically.
    // Offering a moment here would stack an overlay on top of the tally, which
    // is the very thing the Director exists to stop.
    showNextRound();
  } else {
    showResults();
  }
}

export function nextQuestion(): void {
  const engine = ctx.engine!;
  if (roundCompleting) return; // the boundary already fired for this round (stale advance / duplicate call)
  if (engine.idx + 1 >= engine.n) {
    roundCompleting = true; // cleared when the next round renders (or at the next run start)
    void onRoundComplete();
    return;
  }
  if (prerollIv) clearInterval(prerollIv);
  if (prerollTo) clearTimeout(prerollTo);
  const qNext = engine.idx + 2;
  const catNext = engine.order[engine.idx + 1].category;
  byId('prerollQ').textContent = `Question ${qNext} of ${engine.n}`;
  byId('prerollCat').textContent = catNext;
  show('preroll');

  // The full numbered dial is reserved for a section's Q1 (engine.idx is still -1,
  // nothing served yet this round); every other question gets the quiet pause below.
  const sectionStart = engine.idx === -1;
  byId('screenPreroll').classList.toggle('quiet', !sectionStart);
  if (!sectionStart) {
    prerollTo = setTimeout(() => {
      prerollTo = null;
      serveQuestion();
    }, PREROLL_QUIET_MS);
    return;
  }

  let c = 3;
  prerollShow(c);
  prerollIv = setInterval(() => {
    c--;
    if (c <= 0) {
      if (prerollIv) clearInterval(prerollIv);
      prerollIv = null;
      serveQuestion();
    } else {
      prerollShow(c);
    }
  }, PREROLL_TICK_MS);
}

function prerollShow(n: number): void {
  const w = byId('prerollWrap');
  byId('prerollCount').textContent = String(n);
  w.classList.remove('run', 'n1', 'n2', 'n3');
  void w.offsetWidth;
  w.classList.add('run', 'n' + n);
  sndTick();
}

function serveQuestion(): void {
  const engine = ctx.engine!;
  const now = performance.now();
  const q = engine.serveNext(now);
  recordServedCode(questionCode(q)); // stable-code the served question for /api/log/served
  answered = false;
  lastTickSecond = -1;
  byId('catChip').textContent = q.category;
  // Ambient ichthys behind Chosen questions only — syncFishSchool is a no-op
  // when the state is already right, so the school never restarts mid-crossing.
  syncFishSchool(document.getElementById('screenPlay'), isChosenCategory(q.category));
  syncArkParade(
    document.getElementById('screenPlay'),
    isBibleStoriesRound(ROUNDS[ctx.game.roundIndex]?.name, roundGroupKey),
  );
  renderStretchBadge(q.stretch === true);
  byId('qProg').textContent = hudProgress(engine.questionNumber, engine.n);
  byId('questionBody').textContent = q.body;
  byId('revealBox').innerHTML = '';
  byId('screenPlay').classList.remove('flashG', 'flashR');
  byId('meterValue').classList.remove('frozen');
  const grid = byId('optionsGrid');
  grid.innerHTML = '';
  // Long answers ("Transubstantiation") overflow the 2-up grid — stack them full-width.
  grid.classList.toggle('long', q.options.some((o) => o.length >= 16));
  q.options.forEach((opt, i) => {
    const b = document.createElement('button');
    b.className = 'opt';
    b.innerHTML = `<span class="key">${i + 1}</span><span>${esc(opt)}</span>`;
    b.addEventListener('click', () => onAnswer(i));
    grid.appendChild(b);
  });
  // Theo's 50/50 is once per round — and hidden through Round 1 so the warm-up
  // stays simple. Reflect availability; introduce it the first time it appears.
  const hb = document.getElementById('hintBtn') as HTMLButtonElement | null;
  if (hb) {
    const visible = hintVisibleFor(ctx.game.roundIndex);
    hb.classList.toggle('hidden', !visible);
    hb.disabled = hintUsedThisRound;
    if (visible) maybeIntroduceHint();
  }
  show('play');
  renderMeter();
  renderTimer = setInterval(renderMeter, 100);
  timeoutTimer = setTimeout(onTimeout, engine.currentTimeoutMs); // scales with hard-question runway
}

/** Show/hide the ⭐ STRETCH badge for the question just served. The label is
    deliberately explicit about BOTH halves of the deal — extra value, no risk —
    because that promise is the whole point of the on-ramp (plan §3b). */
function renderStretchBadge(on: boolean): void {
  const el = document.getElementById('stretchBadge');
  if (!el) return;
  el.classList.toggle('hidden', !on);
  if (on) track('StretchQuestionServed', { round: ctx.game.roundIndex + 1 });
}

function renderMeter(): void {
  const engine = ctx.engine!;
  const now = performance.now();
  const value = engine.currentValueCents(now);
  byId('meterValue').textContent = fmtCents(value);
  const low = value <= 0;
  byId('meterValue').classList.toggle('low', low);
  byId('meterValue').classList.toggle('flicker', value === 0);
  (byId('barFill') as HTMLElement).style.width =
    Math.max(0, Math.min(100, (value / engine.currentStartCents) * 100)) + '%';
  byId('barFill').classList.toggle('low', low);
  const elapsed = now - engine.servedAt;
  const sec = Math.floor(elapsed / 1000);
  if (sec !== lastTickSecond && sec >= 1 && value > FLOOR_CENTS) {
    lastTickSecond = sec;
    sndTick();
  }
}

function stopTimers(): void {
  if (renderTimer) clearInterval(renderTimer);
  renderTimer = null;
  if (timeoutTimer) clearTimeout(timeoutTimer);
  timeoutTimer = null;
}

function onAnswer(chosenIndex: number): void {
  if (answered) return;
  if (leaveSheet.isOpen()) return; // the leave sheet is modal — the 1-4 hotkeys must not answer behind it
  answered = true;
  stopTimers();
  reveal(ctx.engine!.submitAnswer(chosenIndex, performance.now()), chosenIndex);
}

function onTimeout(): void {
  if (answered) return;
  answered = true;
  stopTimers();
  reveal(ctx.engine!.timeout(performance.now()), -1);
}

function reveal(result: AnswerResult, chosenIndex: number): void {
  const engine = ctx.engine!;
  const code = questionCode(engine.current!);
  const isStretch = engine.current!.stretch === true;
  // The Director's prayer-break cadence rides on a rolling LIFETIME answered
  // counter, so it is bumped here — at the one place every answer passes through.
  momentDirector.noteAnswered();
  track('QuestionAnswered', {
    code,
    correct: result.wasCorrect,
    ms: Math.round(result.elapsedMs),
    category: engine.current!.category, // v1.1.0: category prop (quest eval + dashboards)
  });
  // Per-answer submission (AUTHED only) — writes app.UserQuestionHistory, which is
  // what makes the server's least-recently-seen ordering work and finally ends the
  // silent repeats. Queued + fire-and-forget: never blocks the reveal. Guests keep
  // exactly today's experience (telemetry + /log/served only).
  if (isAuthenticated() && serverSessionId) {
    recordAnswer({
      sessionId: serverSessionId,
      stableCode: code,
      // The SOURCE-order index, never the shuffled one the screen shows: the
      // server scores against QuestionRevisions.CorrectIndex (authored order).
      selectedIndex: result.sourceChosenIndex,
      correct: result.wasCorrect,
      elapsedMs: Math.round(result.elapsedMs),
      valueCents: result.deltaCents,
    });
  }
  byId('meterValue').classList.remove('flicker');
  byId('meterValue').classList.add('frozen');
  byId('optionsGrid')
    .querySelectorAll('.opt')
    .forEach((b, i) => {
      (b as HTMLButtonElement).disabled = true;
      if (i === result.correctIndex) b.classList.add('correct');
      else if (i === chosenIndex) b.classList.add('wrong');
      else b.classList.add('dim');
    });
  if (result.wasCorrect) {
    byId('screenPlay').classList.add('flashG');
    // Freeze the meter to the exact amount banked. renderMeter samples on a 100ms
    // interval, so at the click it can read up to ~$10 higher than the value the
    // engine actually banks at the click timestamp — leaving the frozen meter and
    // the bank pill disagreeing (QA bug: "$645 shown, $637 credited"). Painting the
    // banked delta here makes displayed == floatDelta == bank change.
    byId('meterValue').textContent = fmtCents(result.deltaCents);
    sndCorrect();
    applause();
    // Banked the full $1,000 — a beat-the-hold answer earns a confetti moment.
    if (result.deltaCents >= 100000) thousandConfetti();
    // Correct-run pop: Theo cheers on runs of 3/6/9… (corner pop fits inside
    // the 5s reveal window; capped at 4/game so it never over-interrupts).
    // Gated on the staged introduction — a 3-run can land before the Q5 break,
    // and Theo must never appear before he has introduced himself.
    correctRun++;
    // A ⭐ stretch question earns its OWN word — reaching above the cap is the
    // moment worth marking, so it takes precedence over the run pop.
    if (isTheoIntroduced() && isStretch) {
      track('TheoShown', { moment: 'stretch-win', character: 'theo' });
      triggerCharPop(getLine('theo', 'stretch-win'));
    } else if (isTheoIntroduced() && correctPopShouldFire(correctRun, theoPopsThisGame)) {
      theoPopsThisGame++;
      track('TheoShown', { moment: 'correct', character: 'theo' });
      triggerCharPop(getLine('theo', 'correct'));
    }
  } else {
    byId('screenPlay').classList.add('flashR');
    sndWrong();
    correctRun = 0; // a miss breaks the run
    // Missing a stretch costs nothing (the engine clamped the delta to $0) —
    // Theo says so out loud, so "no risk" is felt and not just claimed.
    if (isTheoIntroduced() && isStretch) {
      track('TheoShown', { moment: 'stretch-miss', character: 'theo' });
      triggerCharPop(getLine('theo', 'stretch-miss'));
    }
  }
  updateBank();
  if (result.deltaCents !== 0) floatDelta(result.deltaCents);

  const secs = (result.elapsedMs / 1000).toFixed(1);
  let label: string;
  if (result.wasCorrect) label = 'Correct';
  else if (isStretch) label = 'Wrong, and a stretch never costs you a dollar';
  else if (result.deltaCents === 0) label = 'Wrong, but no penalty. You beat the clock';
  else label = result.timedOut ? 'Timed out' : 'Wrong. The clock hit zero';

  const citation = cleanCitation(result.citation);
  byId('revealBox').innerHTML =
    `<div class="cite">${label} · ${secs}s · ${esc(citation)}` +
    `<button class="cite-info" id="citeInfoBtn" aria-label="About this source">i</button></div>`;
  const correctText = engine.current!.options[result.correctIndex];
  (byId('citeInfoBtn') as HTMLButtonElement).onclick = (e) => {
    e.stopPropagation();
    openSourcePop(correctText, citation);
  };

  if (result.timedOut && !angelShownThisRound) {
    angelShownThisRound = true;
    triggerAngel();
  }

  advanceTimer = setTimeout(advanceAfterReveal, REVEAL_MS);
}

/**
 * The question boundary — and the Moment Director's main seat. Each system still
 * decides its OWN eligibility here (breakAt, registerPromptShouldFire,
 * dykShouldFire); the Director decides WHICH of the eligible ones actually runs,
 * and grants at most one. Anything declined is only deferred: the register/DYK
 * thresholds are `>=`, and the break opportunity below is `>=` too, so it asks
 * again at the next reveal instead of being lost for the round.
 */
function advanceAfterReveal(): void {
  advanceTimer = null; // fired — the leave sheet must not see a stale handle and re-arm a second advance
  byId('meterValue').classList.remove('frozen');
  const engine = ctx.engine!;
  const rc = ROUNDS[ctx.game.roundIndex];
  // Prior rounds' questions land in ctx.game.log at onRoundComplete, so this
  // sum is the exact whole-game question count at this reveal.
  const cumulative = ctx.game.log.filter((r) => !r.bonus).length + engine.questionNumber;

  // What each system says it is eligible for, right now.
  const candidates: MomentKind[] = ['prayer']; // cadence is the Director's own (≥30 answered)
  const breakOpportunity = engine.questionNumber >= rc.breakAt && !engine.breakDone;
  if (breakOpportunity) candidates.push('minigame');
  if (dykShouldFire(cumulative, dykFired)) candidates.push('feastdyk');
  // GTKY (PLAN-gameplay-v2 §6): the ~50-answer cadence is the Director's own, so
  // this only answers "may we ask THIS player at all today?" — engaged + authed,
  // one per day, and something still left to ask. Its slot sits BELOW prayer,
  // breaks and feast/DYK: a quiet moment never loses to a survey.
  // Guests short-circuit before the pool scan / storage reads — this runs at
  // every reveal, and the gate says no to them anyway.
  if (isAuthenticated()) {
    const gtkyOffer = gtkyShouldOffer({
      ...gtkyEngagement(),
      authed: true,
      careerLevel,
      poolRemaining: gtkyPoolRemaining(),
      lastAskedDay: readLastAskedDay(),
      today: dayStamp(new Date()),
    });
    if (gtkyOffer) candidates.push('gtky');
  }
  if (registerPromptShouldFire(cumulative, registerPromptShown, isAuthenticated())) candidates.push('register');

  // Reverence spacing: a prayer break never sits next to the money wheel, so it
  // steps aside for one slot whenever the wheel is the break in play.
  const grant = momentDirector.consult(candidates, {
    wheelAdjacent: breakOpportunity && rc.breakType === 'wheel',
  });
  if (grant) track('MomentGranted', { kind: grant, round: ctx.game.roundIndex + 1, cumulative });

  // ONBOARDING EXEMPTION: a brand-new player meets Brother Theo at their first
  // break (showBreak below), so that one break is never declined by the 10-minute
  // rule — it is an introduction, not a pacing beat. It still commits to the
  // Director, so the clocks reset exactly as a granted break would.
  if (!grant && breakOpportunity && !isTheoIntroduced()) {
    momentDirector.grant('minigame');
    track('MomentGranted', { kind: 'minigame', round: ctx.game.roundIndex + 1, cumulative, seam: 'intro' });
    showBreak(rc.breakType);
    return;
  }

  if (grant === 'prayer') {
    void runPrayerBreak().then(() => nextQuestion());
    return;
  }
  if (grant === 'minigame') {
    showBreak(rc.breakType);
    return;
  }
  if (grant === 'register') {
    registerPromptShown = true;
    lastPromptCumulative = cumulative;
    track('RegisterPromptShown', { cumulative });
    openRegisterPrompt(onRegisterPromptCreate, onRegisterPromptGuest);
    return;
  }
  if (grant === 'feastdyk') {
    dykFired++;
    track('DidYouKnowShown', { cumulative });
    // Theo frames the fact with a registry 'dyk' line (corner pop) as it rises —
    // only once he has been introduced (the first-break intro precedes the first
    // DYK threshold, so in practice this is always true after Q5).
    if (isTheoIntroduced()) {
      track('TheoShown', { moment: 'dyk', character: 'theo' });
      triggerCharPop(getLine('theo', 'dyk'));
    }
    triggerDidYouKnow(() => nextQuestion());
    return;
  }
  if (grant === 'gtky') {
    // The Director stamped the 50-answer mark on the GRANT, so whatever happens
    // in the overlay (answer / decline / "Maybe later") the next ask is 50 away.
    // triggerGtkyMoment owns the day stamp + GtkyAsked telemetry; it also calls
    // done() straight through if the pool emptied between offer and grant.
    triggerGtkyMoment(() => nextQuestion());
    return;
  }
  nextQuestion();
}

/* ---------- Theo's 50/50 hint ---------- */
const HINT_INTRO_KEY = 'catholics.hintIntroSeen';

/** The first time the 50/50 becomes visible (Round 2, first question), Theo
    warmly introduces it — once ever (localStorage flag). */
function maybeIntroduceHint(): void {
  try {
    if (localStorage.getItem(HINT_INTRO_KEY) === '1') return;
    localStorage.setItem(HINT_INTRO_KEY, '1');
  } catch {
    return; // storage unavailable — skip the intro rather than risk repeating it
  }
  triggerCharPop("See my 50/50? When you're stuck, tap it and I'll clear two wrong answers. Free once each round.");
}

/** Remove two wrong options for the current question. Free once per round; the
    timer keeps ticking (no engine change — the drain curve already prices
    deliberation). Wired to the play-screen button + the "h" key in main.ts. */
export function useHint(): void {
  const engine = ctx.engine;
  if (!engine || !engine.current) return;
  if (answered) return; // reveal already showing — nothing to trim
  if (leaveSheet.isOpen()) return; // paused behind the leave sheet (H hotkey)
  if (!hintVisibleFor(ctx.game.roundIndex)) return; // hidden in Round 1 — also guard the hotkey
  if (!hintAllowed(hintUsedThisRound)) return;
  hintUsedThisRound = true;
  const removals = pickHintRemovals(engine.current.correctIndex, engine.current.options.length);
  const opts = byId('optionsGrid').querySelectorAll<HTMLButtonElement>('.opt');
  removals.forEach((i) => {
    const b = opts[i];
    if (b) {
      b.classList.add('hint-out');
      b.disabled = true;
    }
  });
  track('TheoHintUsed', { code: questionCode(engine.current) });
  triggerCharPop(getLine('theo', 'hint'));
  const btn = document.getElementById('hintBtn') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
}

/* ---------- Register prompt (guest → account) ---------- */
function onRegisterPromptGuest(): void {
  track('RegisterPromptDeclined', { cumulative: lastPromptCumulative });
  nextQuestion(); // overlay already closed by openRegisterPrompt
}

function onRegisterPromptCreate(): void {
  track('RegisterPromptAccepted', { cumulative: lastPromptCumulative });
  // MID-GAME auth round-trip: hide the bank bar and arm a resume so that after a
  // successful register/login the run picks up exactly where it left off (the
  // engine/ctx state survives the screen swaps). afterAuthSuccess consumes it.
  byId('bankbar').classList.add('hidden');
  setPostAuthResume(resumeGameAfterAuth);
  showRegister();
}

/** Resume a mid-game run after a successful auth round-trip. */
function resumeGameAfterAuth(): void {
  byId('bankbar').classList.remove('hidden');
  nextQuestion(); // drives preroll → play from the surviving engine state
}

/** Display-clean a citation: the scholarly pilcrow (¶) in Catechism references
    reads like a rendering glitch to most players — "CCC 881" is equally standard. */
function cleanCitation(s: string): string {
  return s.replace(/¶\s*/g, '');
}

/* ---------- Play HUD: ◀ pause / "Leave this round?" (PLAN-design-round §A) ----------
   The sheet (ui/leaveRound.ts) owns the words and the open/keep/save machine;
   the controller owns the CLOCK. Three live states can be paused:
     question — the meter is draining: stop the 100ms paint + the timeout, and
                on resume shift engine.servedAt by the paused span so the value
                and the remaining runway pick up exactly where they stopped;
     reveal   — the 5s linger before advanceAfterReveal: hold it, re-arm it;
     preroll  — the 3-2-1 dial / quiet pause: cancel it, and on resume restart
                it (nextQuestion re-runs the dial for the same question).
   "Save & leave" writes the device snapshot for the CURRENT round (resume is
   round-granular — the round in progress restarts from Q1) and goes home. */
type LeavePause = { kind: 'question'; pausedAt: number } | { kind: 'reveal' } | { kind: 'preroll' } | { kind: 'none' };
let leavePause: LeavePause | null = null;

function pauseForLeave(): void {
  const engine = ctx.engine;
  if (prerollIv || prerollTo) {
    if (prerollIv) clearInterval(prerollIv);
    if (prerollTo) clearTimeout(prerollTo);
    prerollIv = null;
    prerollTo = null;
    leavePause = { kind: 'preroll' };
    return;
  }
  if (engine && engine.current && !answered && renderTimer) {
    stopTimers();
    leavePause = { kind: 'question', pausedAt: performance.now() };
    return;
  }
  if (answered && advanceTimer) {
    clearTimeout(advanceTimer);
    advanceTimer = null;
    leavePause = { kind: 'reveal' };
    return;
  }
  leavePause = { kind: 'none' };
}

function resumeFromLeave(): void {
  const p = leavePause;
  leavePause = null;
  const engine = ctx.engine;
  if (!p || !engine) return;
  switch (p.kind) {
    case 'question': {
      const now = performance.now();
      engine.servedAt += now - p.pausedAt; // the clock did not run while the sheet was up
      renderMeter();
      renderTimer = setInterval(renderMeter, 100);
      const remaining = Math.max(0, engine.currentTimeoutMs - (now - engine.servedAt));
      timeoutTimer = setTimeout(onTimeout, remaining);
      break;
    }
    case 'reveal':
      advanceTimer = setTimeout(advanceAfterReveal, REVEAL_MS);
      break;
    case 'preroll':
      nextQuestion();
      break;
    case 'none':
      break;
  }
}

function saveAndLeave(info: LeaveInfo): void {
  stopTimers();
  if (prerollIv) clearInterval(prerollIv);
  if (prerollTo) clearTimeout(prerollTo);
  prerollIv = null;
  prerollTo = null;
  if (advanceTimer) clearTimeout(advanceTimer);
  advanceTimer = null;
  leavePause = null;
  // Round 1 with nothing finished has nothing worth resuming — a snapshot there
  // would only relabel the Start button "Resume — Round 1 · $0". Clear instead.
  const nothingToSave = info.roundIndex === 0 && ctx.game.log.length === 0;
  if (nothingToSave) clearSnapshot();
  else saveRunSnapshot(info.roundIndex); // CURRENT round: "restart Round N with your log/bank"
  void flushServed();
  void flushAnswers();
  track('RoundLeft', {
    round: info.roundIndex + 1,
    question: info.question,
    of: info.total,
    bank: ctx.game.bank,
    saved: !nothingToSave,
  });
  // Same exit as accountMenu.goHome (not imported — that module imports us): a
  // mid-game auth round-trip may have armed a resume; leaving is an explicit exit.
  consumePostAuthResume();
  byId('bankbar').classList.add('hidden');
  showHome();
}

export const leaveSheet = createLeaveSheet({
  onPause: pauseForLeave,
  onResume: resumeFromLeave,
  onSave: saveAndLeave,
  view: renderLeaveSheet,
});

/** Open "Leave this round?" for the round in progress (HUD ◀ and the account
    menu's mid-round item). No-op when no round is live. */
export function openLeaveRound(): void {
  const engine = ctx.engine;
  if (!engine) return;
  const idx = ctx.game.roundIndex;
  leaveSheet.open({
    roundIndex: idx,
    roundName: ROUNDS[idx]?.name ?? '',
    bankCents: ctx.game.bank,
    question: Math.max(1, engine.questionNumber),
    total: engine.n,
  });
}

/** Wire the play HUD once at boot (after the shell HTML is in the DOM):
    mounts the leave sheet and binds ◀. Called from accountMenu.initAccountMenu. */
export function initPlayHud(): void {
  mountLeaveSheet(leaveSheet);
  document.getElementById('hudBackBtn')?.addEventListener('click', openLeaveRound);
}

/** 🔥 cell: authed players' current daily streak, read the way pathHome's chip
    does (profile/me stats). One light fetch per round; guests hide the cell. */
/** The streak as it stood when THIS game began — the "before" for the milestone
    moment. Captured on the first HUD refresh of a run (before any round-complete
    can bump it) and cleared in prepareRun(). null = unknown (guest / fetch failed). */
let gameStartStreak: number | null = null;
/** The profile XP when THIS run began — the "before" for the results rank-up.
    Captured alongside gameStartStreak; null = unknown (guest / fetch failed). */
let gameStartXp: number | null = null;

function refreshHudStreak(): void {
  if (!isAuthenticated()) {
    setHudStreak(null, false);
    return;
  }
  void getProfileMe().then((r) => {
    if (!r.ok || !r.data.stats) return;
    const streak = r.data.stats.currentStreak || 0;
    if (gameStartStreak === null) gameStartStreak = streak;
    if (gameStartXp === null && typeof r.data.stats.xp === 'number') gameStartXp = r.data.stats.xp;
    setHudStreak(streak, true);
  });
}

/* ---------- Source popover (the reveal's ⓘ icon) ---------- */
function openSourcePop(correctText: string, citation: string): void {
  if (advanceTimer) clearTimeout(advanceTimer);
  advanceTimer = null; // hold the game while reading
  byId('spAnswer').textContent = correctText;
  byId('spCite').textContent = citation;
  const pop = byId('sourcePop');
  pop.classList.remove('hidden');
  pop.setAttribute('aria-hidden', 'false');
}

export function closeSourcePop(): void {
  const pop = byId('sourcePop');
  if (pop.classList.contains('hidden')) return;
  pop.classList.add('hidden');
  pop.setAttribute('aria-hidden', 'true');
  advanceTimer = setTimeout(advanceAfterReveal, REVEAL_RESUME_MS);
}

export function isSourcePopOpen(): boolean {
  return !byId('sourcePop').classList.contains('hidden');
}

/* ---------- Breaks ---------- */

/** One feast-moment lookup per boot — a day with no feast must not re-hit the
    endpoint at every break of every game. */
let feastAnnounceChecked = false;
const FEAST_LOOKUP_TIMEOUT_MS = 2_500; // a slow network never holds the break hostage

/** "Today is a feast day" interrupt, at the same seam as Theo's introduction.
    All gating is pure (feastAnnouncement.ts); the local half runs first so the
    vast majority of players never trigger the network call at all. Whatever
    happens — not eligible, no feast today, offline, slow — `then` runs exactly
    once and the break proceeds. */
function maybeAnnounceFeast(then: () => void): void {
  if (feastAnnounceChecked) {
    then();
    return;
  }
  feastAnnounceChecked = true;

  if (!feastAnnouncementEligible(isAuthenticated(), getGamesCompleted(), isTheoIntroduced(), wasAnnouncedToday())) {
    then();
    return;
  }

  let resumed = false;
  const resume = (): void => {
    if (resumed) return;
    resumed = true;
    then();
  };
  const guard = setTimeout(resume, FEAST_LOOKUP_TIMEOUT_MS);

  void getFeastMoment(etDateString())
    .then((r) => {
      if (resumed) return; // the timeout guard already released the break
      clearTimeout(guard);
      const moment = r.ok ? r.data : null; // ok + null data = a valid "no feast today"
      if (
        !moment ||
        !shouldShowFeastAnnouncement({
          isAuthed: isAuthenticated(),
          gamesCompleted: getGamesCompleted(),
          theoIntroduced: isTheoIntroduced(),
          feastMoment: moment,
          alreadyShownToday: wasAnnouncedToday(),
        })
      ) {
        resume();
        return;
      }
      resumed = true; // the overlay owns the continuation from here
      markFeastAnnounced(); // at most once per ET day (private mode: may repeat next boot)
      track('FeastAnnouncementShown', { feastDayId: moment.feastDayId });
      triggerFeastAnnouncement(moment, then);
    })
    .catch(() => resume());
}

function showBreak(type: BreakType): void {
  // Staged introduction: a brand-new player meets Brother Theo HERE — at the
  // first break of their first game, before the mini-game shows. One-time.
  if (!isTheoIntroduced()) {
    markTheoIntroduced();
    track('TheoIntroduced', { seam: 'break', breakType: type });
    const line =
      type === 'wheel'
        ? "Before you spin, I'm Brother Theo. I walk with every pilgrim on this road, and I'll pop in with a word of encouragement now and then. Enjoy the wheel."
        : "A quick hello before your break. I'm Brother Theo. I walk with every pilgrim on this road, and I'll pop in with a word of encouragement now and then. Enjoy it.";
    triggerTheoIntro(line, () => runBreak(type));
    return;
  }
  // Theo is already known → this player is eligible for the feast check. The two
  // are independent and never both fire for the same player in the same moment:
  // meeting Theo returns above, and a player who just met him this game is far
  // below the games-completed bar anyway.
  maybeAnnounceFeast(() => runBreak(type));
}

/** Every granted mini-game break opens with the skippable pre-card (plan §3):
    "🕹️ Break time — <game>", a primary Play, and a "Skip →" after a beat.
    Skipping forfeits NOTHING — it banks the same graceful pass the mini-games'
    own skip links do — and the Director already reset the break clock when it
    granted the moment, so a skipped break still counts as the pause. The
    mini-game modules themselves are untouched. */
function runBreak(type: BreakType): void {
  void showBreakCard(type).then((choice) => {
    if (choice === 'skip') {
      track('BreakSkipped', { type });
      ctx.engine?.awardBonus(BREAK_SKIP_AWARD, 'a peaceful pass', 'Skip'); // also marks breakDone
      ctx.updateBank();
      ctx.floatDelta(BREAK_SKIP_AWARD);
      nextQuestion();
      return;
    }
    track('BreakPlayed', { type });
    if (type === 'manna') showManna();
    else if (type === 'match3') showMatch3();
    else if (type === 'marymatch3') showMaryMatch3();
    else showBonusWheel();
  });
}

/* ---------- Next round / difficulty ---------- */
/** The choice currently highlighted (Balanced by default). Committing happens
    only on the explicit Start button (Eric 2026-09-06 restyle). */
let nrSelected: 'easier' | 'same' | 'harder' = 'same';

/** Reflect nrSelected onto the choice cards + the Hard telegraph line. */
function paintNrSelection(): void {
  document.querySelectorAll<HTMLButtonElement>('.nr-choice').forEach((b) => {
    b.classList.toggle('picked', b.dataset.d === nrSelected);
    b.setAttribute('aria-pressed', String(b.dataset.d === nrSelected));
  });
  // One honest line about what hard mode actually costs, telegraphed BEFORE
  // the opt-in (PLAN-gameplay-v2 §3b) — shown while Hard is the selection.
  const hardNote = document.getElementById('nrHardNote');
  if (hardNote) {
    hardNote.textContent = HARD_MODE_SUBTITLE;
    hardNote.classList.toggle('show', nrSelected === 'harder');
  }
}

function showNextRound(): void {
  nrChosen = false;
  nrSelected = 'same';
  byId('nrTitle').textContent = `Round ${ctx.game.roundIndex + 1} Complete`;
  byId('nrBank').textContent = fmtCents(ctx.game.bank);
  byId('nrQuip').textContent = '';
  byId('nrQuip').classList.remove('show');
  // Round progress dots — done rounds filled, the rest open.
  const prog = document.getElementById('nrProgress');
  if (prog) {
    const done = ctx.game.roundIndex + 1;
    prog.innerHTML = ROUNDS.map((_, i) => `<span class="nr-dot${i < done ? ' done' : ''}"></span>`).join('');
    prog.setAttribute('aria-label', `${done} of ${ROUNDS.length} rounds complete`);
  }
  const start = document.getElementById('nrStartBtn') as HTMLButtonElement | null;
  if (start) {
    start.textContent = `Start Round ${ctx.game.roundIndex + 2} →`;
    start.disabled = false;
  }
  document.querySelectorAll<HTMLButtonElement>('.nr-choice').forEach((b) => (b.disabled = false));
  paintNrSelection();
  show('nextround');
}

/** A choice card tap SELECTS (re-selectable until Start commits). */
export function pickDifficulty(choiceEl: HTMLElement): void {
  if (nrChosen) return;
  const d = choiceEl.dataset.d;
  if (d === 'easier' || d === 'same' || d === 'harder') nrSelected = d;
  paintNrSelection();
}

/** The Start button commits the selection and launches the next round. */
export function startNextRound(): void {
  if (nrChosen) return;
  nrChosen = true;
  document.querySelectorAll<HTMLButtonElement>('.nr-choice').forEach((b) => (b.disabled = true));
  const start = document.getElementById('nrStartBtn') as HTMLButtonElement | null;
  if (start) start.disabled = true;
  const nextName = ROUNDS[ctx.game.roundIndex + 1].name;
  const quips: Record<string, string> = {
    easier: 'Gentle it is. Loading "' + nextName + '"…',
    same: 'Steady as she goes. Loading "' + nextName + '"…',
    harder: 'Level 3, the hard stuff. Loading "' + nextName + '"…',
  };
  pendingHard = nrSelected === 'harder'; // REAL: the next round serves the hard pack (easier/same stay cosmetic)
  track('DifficultyPicked', { d: nrSelected });
  byId('nrQuip').textContent = quips[nrSelected] || quips.same;
  byId('nrQuip').classList.add('show');
  sndCorrect();
  nextRoundTimer = setTimeout(() => {
    nextRoundTimer = null;
    void beginRound(ctx.game.roundIndex + 1);
  }, 1400);
}

/** Cancel the between-rounds launch beat (startNextRound's 1.4s "Loading…"):
    "End game & go Home" inside that window must not pop the next round's intro
    over the home screen. Called from accountMenu.goHome and at every run start. */
export function abortPendingRound(): void {
  if (nextRoundTimer) clearTimeout(nextRoundTimer);
  nextRoundTimer = null;
}

/* ---------- Results ---------- */
function showResults(): void {
  clearSnapshot(); // the game finished — nothing left to resume
  // Edge case: a game that somehow finished without ever hitting the intro seam
  // (e.g. resumed past the first break) still counts as having met Theo — the
  // home must progress to the greeting-card stage.
  if (!isTheoIntroduced()) {
    markTheoIntroduced();
    track('TheoIntroduced', { seam: 'results' });
  }
  // The res-hero shows the final bank, so drop the redundant top bank bar here —
  // this also keeps the top-right account button clear of the bank pill.
  byId('bankbar').classList.add('hidden');
  show('results');
  const bank = ctx.game.bank;
  const questions = ctx.game.log.filter((r) => !r.bonus);
  const totalQ = questions.length;
  const correct = questions.filter((r) => r.wasCorrect).length;
  const accuracy = totalQ ? Math.round((correct / totalQ) * 100) : 0;
  const best = Math.max(0, ...ctx.game.log.map((r) => r.deltaCents));

  byId('finalBank').textContent = fmtCents(bank);
  byId('finalBank').classList.toggle('neg', bank < 0);
  byId('resHero').classList.toggle('neg', bank < 0);

  const m = medalFor(bank, accuracy);
  byId('resEmoji').innerHTML = m.iconHtml;
  byId('resMedal').textContent = `${m.tier.charAt(0).toUpperCase() + m.tier.slice(1)} · ${m.label}`;
  byId('resMedal').className = 'res-medal ' + m.tier;
  byId('resHeadline').textContent = m.headline;
  (byId('resHeadline') as HTMLElement).style.color = bank < 0 ? 'var(--red)' : 'var(--gold)';

  const ach = ctx.game.achievements || [];
  byId('resAchTitle').classList.toggle('hidden', ach.length === 0);
  byId('resAch').classList.toggle('hidden', ach.length === 0);
  byId('resAch').innerHTML = ach
    .map(
      (a) =>
        `<div class="ach-chip"><span class="ach-ic">${isIconName(a.icon) ? icon(a.icon, 24) : esc(a.icon)}</span><span><div class="ach-name">${esc(
          a.name,
        )}</div><div class="ach-desc">${esc(a.desc)}</div></span></div>`,
    )
    .join('');

  byId('resStats').innerHTML =
    `<div class="stat"><span class="v ${correct > 0 ? 'pos' : ''}">${correct}/${totalQ}</span><span class="k">Correct</span></div>` +
    `<div class="stat"><span class="v">${accuracy}%</span><span class="k">Accuracy</span></div>` +
    `<div class="stat"><span class="v gold">${best > 0 ? fmtDelta(best) : '—'}</span><span class="k">Best</span></div>`;

  const list = byId('resList');
  list.innerHTML = '';
  let currentRound: string | undefined;
  ctx.game.log.forEach((r) => {
    if (r.round !== currentRound) {
      currentRound = r.round;
      const h = document.createElement('div');
      h.className = 'res-round-title';
      h.innerHTML = `<span class="rt-ic">${r.roundIcon || ''}</span> ${esc(r.round)}`;
      list.appendChild(h);
    }
    const row = document.createElement('div');
    const dcls = r.deltaCents > 0 ? 'pos' : r.deltaCents < 0 ? 'neg' : 'safe';
    const deltaTxt = r.deltaCents === 0 ? '$0' : fmtDelta(r.deltaCents);

    if (r.bonus) {
      row.className = 'res-row bonus';
      const line =
        r.category === 'Manna'
          ? `Filled your basket in <b>${esc(r.body)}</b>`
          : r.category === 'Match3'
            ? `Cleared <b>${esc(r.body)}</b> from the board`
            : r.category === 'Mary'
              ? `Mother Mary Match-3, completed <b>${esc(r.body)}</b>`
              : r.category === 'Coffee'
                ? `Holy Coffee, a blessed cup`
                : r.category === 'Sneeze'
                  ? `A saintly sneeze. <b>Bless you.</b>`
                  : r.category === 'Skip'
                    ? `Took a peaceful pass on the break`
                    : `Wheel landed on <b>${esc(r.body)}</b>`;
      row.innerHTML =
        `<div class="res-badge star">★</div>` +
        `<div class="res-main">` +
        `<div class="res-qhead"><span class="res-cat">${esc(r.category)}</span><span class="res-delta ${dcls}">${deltaTxt}</span></div>` +
        `<div class="res-bonus-line">${line}</div>` +
        `</div>`;
    } else {
      row.className = 'res-row';
      const badge = r.wasCorrect ? '<div class="res-badge ok">✓</div>' : '<div class="res-badge bad">✕</div>';
      let ansLine: string;
      if (r.wasCorrect) {
        ansLine = `<span class="res-tag">You:</span> <span class="res-you ok">${esc(r.yourAnswer)}</span>`;
      } else if (r.timedOut) {
        ansLine = `<span class="res-tag">Timed out</span> <span class="res-tag">→</span> <span class="res-correct">${esc(r.correctAnswer)}</span>`;
      } else {
        ansLine = `<span class="res-you bad">${esc(r.yourAnswer)}</span> <span class="res-tag">→</span> <span class="res-correct">${esc(r.correctAnswer)}</span>`;
      }
      row.innerHTML =
        badge +
        `<div class="res-main">` +
        `<div class="res-qhead"><span class="res-cat">${r.qNum} · ${esc(r.category)}</span><span class="res-delta ${dcls}">${deltaTxt}</span></div>` +
        `<div class="res-q">${esc(r.body)}</div>` +
        `<div class="res-ans">${ansLine}<span class="res-time">${(r.elapsedMs / 1000).toFixed(1)}s</span></div>` +
        `</div>`;
    }
    list.appendChild(row);
  });

  // Reaching results = a full game completed: log it, unlock the Arcade, and
  // surface the callout. (flushServed posts any remaining served-question codes.)
  track('GameCompleted', { bank, accuracy, medal: m.tier });
  recordGameCompleted(); // device counter — ends the first-game warm-up (easyStart.ts)
  // "Arcade unlocked!" is a one-time announcement, not a permanent fixture —
  // showing it every game duplicated the journey card's arcade CTA (Eric
  // 2026-08-16, "2 buttons to keep playing the Arcade").
  const arcadeNewlyUnlocked = !isArcadeUnlocked();
  unlockArcade();
  void flushServed();
  void flushAnswers(); // the final round's answers (and any retries) — nothing else flushes them after this
  byId('resArcadeCta').classList.toggle('hidden', !arcadeNewlyUnlocked);

  // Signed-in players persist server-side (server is authoritative) and can view history.
  byId('resultsHistoryBtn').classList.toggle('hidden', !isAuthenticated());
  const payload = {
    clientVersion: CLIENT_VERSION,
    finalBankCents: bank,
    accuracy,
    medalTier: m.tier,
    rounds: [],
    // server grants by stable catalog Key (usp_Achievement_Grant); names are display-only
    achievements: ach.map((a) => serverKeyFor(a.name)).filter((k): k is string => k !== null),
  };
  // (The memorial candle is lit ONLY by the prayer act now — see candlePrayer.ts.
  // Finishing a game no longer auto-lights it, keeping game mechanics clear of
  // the memorial.)

  const guestCta = byId('resGuestCta');
  if (isAuthenticated()) {
    guestCta.classList.add('hidden');
    // Chain the (previously fire-and-forget) session save → re-read the profile →
    // compare rank before/after (xpBefore = xp − xpAwarded) → celebrate a rank-up.
    void completeSession(payload).then((res) => {
      if (!res.ok) return;
      // Streak milestone (3/7/14/30) — the server is authoritative on what was
      // PAID (db/050 pays once per streak, at /round-complete, where the moment
      // already played — see onRoundComplete). The client-side milestoneFor guess
      // survives only for a run that never opened a server session (legacy
      // /complete-summary path). Nothing is minted here (ui/streak.ts is a moment).
      const ms = resultsStreakMilestone(res.data, serverSessionId !== null, gameStartStreak);
      if (ms) {
        track('StreakMilestone', { days: ms.days, from: gameStartStreak ?? res.data.currentStreak ?? null, paid: !!ms.reward });
        void showStreakMilestone(ms.days, ms.reward);
      }
      // Rank-up: compare the rank at the START of the game with the rank now.
      // The per-round awards are already in the profile XP, so "xp − completion
      // bonus" (the old before) could never cross a rank.
      const awarded = res.data.xpAwarded || 0;
      if (awarded <= 0 && xpAwardedThisGame <= 0) return;
      void getProfileMe().then((me) => {
        if (!me.ok || !me.data.stats) return;
        setGtkyEngagement(me.data.stats); // streak/sessions just moved — refresh the GTKY gate
        const after = rankUpAfterGame(gameStartXp, me.data.stats.xp, awarded + xpAwardedThisGame);
        if (after) {
          track('RankUpCelebrated', { rankLevel: after.level });
          triggerRankUp(after);
        }
      });
    });
  } else {
    // Guests get a warm nudge: hold the summary so registering (or signing in)
    // from here saves the game they just played.
    setPendingSummary(payload);
    byId('resGuestCtaText').innerHTML =
      `You did well. <b>${fmtCents(bank)}</b> in the bank.<br/>Create a free account so we can save your score.`;
    byId('resGuestCtaText').classList.remove('hidden');
    byId('resSaveScoreBtn').classList.remove('hidden');
    byId('resGuestSaved').classList.add('hidden');
    guestCta.classList.remove('hidden');
    track('GuestResultsCtaShown');
  }

  // Journey Director: point to the next stop on today's pilgrimage (or hide the
  // card when the day's log is complete / opted out).
  renderJourneyResults();
}
